// Pick'em math in one place.
//
// Everything on the Pick'em page comes from two inputs:
//   1. Each pick's MULTIPLIER (1 for a standard pick, e.g. 1.55 for an
//      Underdog boost), read from the feed price by legMultiplier(). Null when
//      the feed doesn't carry it (PrizePicks demons/goblins).
//   2. Each app's PAYOUT TABLE (entry-fee multiples: 3 = $30 back on $10).
//
// From those:
//   • slip breakeven  — the per-pick win rate that makes a slip type break even,
//                       e.g. 2-pick Power at 3x → 3^(-1/2) = 57.7%
//   • pick breakeven  — slip breakeven ÷ pick multiplier
//   • pick EV         — model% ÷ pick breakeven − 1. For Power slips these
//                       compound: slip EV = Π(1 + pick EV) − 1.
//   • slip payout/EV  — payout table × Π multipliers, weighted by the chance
//                       of each number of hits (Flex pays partial hits).
//
// Payout tables change — check them against each app's payout screen.

// Last checked Oct 2026. PrizePicks: official payout table (updated Sept 18,
// 2026). Underdog: third-party guides; 6–8 pick Standard was not confirmed, so
// Standard stops at 5. DraftKings Pick 6: DraftKings' prize page examples.
// Sleeper prices each pick individually, so it has no table.
export const PAYOUTS_CHECKED = 'Oct 2026';

export const PICKEM_PLATFORMS = [
  {
    label: 'Underdog', prefix: 'Underdog', kind: 'table', maxLegs: 8,
    power: { 2: 3, 3: 6, 4: 10, 5: 20 },
    flex: {
      3: { 3: 3, 2: 1 },
      4: { 4: 6, 3: 1.5 },
      5: { 5: 10, 4: 2.5 },
      6: { 6: 25, 5: 2.6, 4: 0.25 },
      7: { 7: 40, 6: 2.75, 5: 0.5 },
      8: { 8: 80, 7: 3, 6: 1 },
    },
  },
  {
    label: 'PrizePicks', prefix: 'PrizePicks', kind: 'table', maxLegs: 6,
    power: { 2: 3, 3: 6, 4: 10, 5: 20, 6: 37.5 },
    flex: {
      2: { 2: 2, 1: 0.5 },
      3: { 3: 3, 2: 1 },
      4: { 4: 6, 3: 1.5 },
      5: { 5: 10, 4: 2, 3: 0.4 },
      6: { 6: 25, 5: 2, 4: 0.4 },
    },
  },
  // Guaranteed Base Multiplier = standard multiplier for the sport and pick
  // count × each pick's multiplier (capped), plus pool "Extra Winnings" on top —
  // so EV here is a floor. DraftKings' prize page shows 3 picks ≥ 6x and
  // 4 picks 10x; other pick counts aren't published, so they're left out
  // rather than guessed. No Flex.
  {
    label: "DraftKings Pick'em", prefix: 'DraftKings (Pick', kind: 'table', maxLegs: 6,
    power: { 3: 6, 4: 10 }, extraWinnings: true, partialTable: true,
  },
  // Each pick carries its own payout multiplier (the feed price); a slip pays
  // their product.
  { label: 'Sleeper', prefix: 'Sleeper', kind: 'perPick', maxLegs: 8 },
];

export const platformByLabel = (label) => PICKEM_PLATFORMS.find(p => p.label === label);
export const platformForBook = (bookName) =>
  PICKEM_PLATFORMS.find(p => (bookName || '').startsWith(p.prefix));

// ── Odds conversions ──

export const americanToDecimal = (price) => {
  if (price == null || price === 0) return null;
  return price > 0 ? 1 + price / 100 : 1 + 100 / Math.abs(price);
};

// Fair American odds for a win probability (negative when p > 50%)
export const probToAmerican = (p) => {
  if (!(p > 0 && p < 1)) return null;
  return p >= 0.5 ? -Math.round((100 * p) / (1 - p)) : Math.round((100 * (1 - p)) / p);
};

// ── Pick multipliers ──

// Books that price picks individually as payout multipliers. The feed encodes
// a multiplier m as decimal odds 1 + m (Underdog +155 → 1.55x boost).
//
// PrizePicks (Demons and Goblins) is NOT priced that way, even though it is on
// this list. OpticOdds sends every demon at exactly +155 and every goblin at
// exactly -155 whatever the player or market — they are category flags, not
// payouts. Live DB check (Oct 2026): 5,439 rows at +155, all binary over-0.5
// style markets (anytime TD / goal scorer, 0.5 goals / assists / points, low
// tackle lines) with exchange-implied win rates spread evenly from 3% to 45%;
// 1,344 rows at -155; none of those player/market combos has a standard
// PrizePicks Flex line. Reading +155 as a 1.55x pick made 149 demon rows
// qualify for Sharp Money (117 with "edge" above 2%) when about one would at a
// realistic per-leg value, and showed e.g. 7.2x for a 2-pick power with two
// demons when PrizePicks pays about 4x. PrizePicks says demon/goblin payouts
// vary per projection and lineup and are only shown in its app (published
// entry tables imply roughly 1.07x–1.17x per demon leg), so the real
// multiplier is unknowable from the feed: those rows get a null multiplier
// (UNPRICED_MULTIPLIER_BOOKS) and are left out of breakeven/EV math. Do not
// re-add the 1.55x / 0.645x reading.
const MULTIPLIER_BOOK_PATTERNS = ['Multipliers', 'Demons and Goblins'];
export const isMultiplierBook = (bookName) =>
  !!bookName && MULTIPLIER_BOOK_PATTERNS.some(p => bookName.includes(p));

// Underdog Multipliers and PrizePicks Demons & Goblins list plain, unboosted
// picks at -137 (√3, the per-pick price of a 3x two-pick) instead of +100. In
// D&G every -137 row is the same line as a PrizePicks Flex row, and they average
// a ~50% model win rate — they are standard 1x picks, not 0.73x discounts.
//
// Underdog is the exception: -137 is also exactly what a real 0.73x discount
// encodes to (1 + 0.73), and Underdog pairs its discounts with a boost on the
// other side of the same line (-147/+155, -161/+199, …). The two cases are
// told apart by the line's TWIN — the opposite side on the same feed:
//   • twin also -137 (or no twin): standard 1x pick. Pregame DB rows in this
//     group averaged a 50.0% consensus win rate (2,336 rows; the ~620 one-sided
//     rows are mostly anytime-TD markets whose complement lives on exchanges).
//   • twin priced differently (typically +135..+141, a ~1.37x boost): a real
//     0.73x discount. These averaged a 67.1% consensus win rate (41 rows), so
//     pricing them at 1x showed them ~160 points too cheap on Sharp Money.
// pickemMultiplierLookup() applies the twin rule; legMultiplier() alone has
// no twin context and keeps the price-only answer.
const STANDARD_PICK_PRICE = -137;
const STANDARD_AT_137 = ['Underdog Fantasy (Multipliers)', 'PrizePicks (Demons and Goblins)'];
const TWIN_RULE_BOOKS = ['Underdog Fantasy (Multipliers)'];
// Multiplier books whose non-standard prices are flags, not payouts (see the
// MULTIPLIER_BOOK_PATTERNS note): any price other than -137 → null multiplier.
const UNPRICED_MULTIPLIER_BOOKS = ['PrizePicks (Demons and Goblins)'];

/**
 * Payout multiplier for one pick, from its feed price alone.
 * Sleeper: the pick's own payout (decimal odds). Everyone else: 1 for a
 * standard pick, or the boost/discount on a multiplier book. PrizePicks
 * demons/goblins: null (payout not in the feed).
 * Prefer pickemMultiplierLookup() when the other rows are available — it
 * resolves the Underdog -137 ambiguity; this treats every -137 as standard.
 */
export function legMultiplier(bookName, price) {
  const platform = platformForBook(bookName);
  if (platform?.kind === 'perPick') return americanToDecimal(price);
  if (!isMultiplierBook(bookName)) return 1;
  if (price === STANDARD_PICK_PRICE && STANDARD_AT_137.some(b => bookName.startsWith(b))) return 1;
  if (UNPRICED_MULTIPLIER_BOOKS.some(b => bookName.startsWith(b))) return null;
  const dec = americanToDecimal(price);
  return dec != null && dec > 1 ? dec - 1 : null;
}

const usesTwinRule = (row) => TWIN_RULE_BOOKS.some(b => (row.bookName || '').startsWith(b));
const oppositeSide = (side) => {
  const s = (side || '').toLowerCase();
  return s === 'over' ? 'under' : s === 'under' ? 'over' : null;
};

/**
 * Context-aware multiplier: indexes every pick'em odds-store row (bookName,
 * price, fixture_id, market_id, playerId, points, side) by line and side and
 * returns multiplierFor(row). Same as legMultiplier() except for the Underdog
 * -137 twin rule above (so PrizePicks demons/goblins are null here too).
 * Build it once per data update and reuse it.
 */
export function pickemMultiplierLookup(rows) {
  const priceByLineSide = new Map();
  for (const row of rows) {
    if (usesTwinRule(row)) priceByLineSide.set(lineKey(row), row.price);
  }
  return (row) => {
    if (row.price === STANDARD_PICK_PRICE && usesTwinRule(row)) {
      const opp = oppositeSide(row.side);
      const twinPrice = opp == null ? undefined : priceByLineSide.get(lineKey({ ...row, side: opp }));
      if (twinPrice !== undefined && twinPrice !== STANDARD_PICK_PRICE) {
        return americanToDecimal(row.price) - 1;   // the boosted twin marks a 0.73x discount
      }
    }
    return legMultiplier(row.bookName, row.price);
  };
}

// ── Hit distributions ──

// P(exactly k hits) for k = 0..n, picks independent (Poisson-binomial)
export function hitDistribution(probs) {
  let dist = [1];
  for (const p of probs) {
    const next = new Array(dist.length + 1).fill(0);
    dist.forEach((q, k) => {
      next[k] += q * (1 - p);
      next[k + 1] += q * p;
    });
    dist = next;
  }
  return dist;
}

// Expected return (entry-fee multiple) of a Flex tier table when every pick wins with p
const flexReturnAtP = (tiers, n, p) =>
  hitDistribution(new Array(n).fill(p)).reduce((sum, q, k) => sum + q * (tiers[k] || 0), 0);

// ── Slip types and breakevens ──

// Per-pick win rate at which a standard-pick slip breaks even
function slipBreakEven(kind, n, tiers) {
  if (kind === 'power') return Math.pow(tiers, -1 / n);
  // Flex: expected return rises with p, so bisect for return = 1
  let lo = 0.01, hi = 0.99;
  if (flexReturnAtP(tiers, n, hi) < 1) return null;
  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2;
    if (flexReturnAtP(tiers, n, mid) < 1) lo = mid; else hi = mid;
  }
  return (lo + hi) / 2;
}

const slipOptionsCache = new Map();   // platform label → options (tables are static)

/**
 * Slip types a breakeven can be quoted against, cheapest first:
 * [{ key, label, be }]. `be` is the standard-pick breakeven for that slip.
 */
export function slipTypeOptions(platform) {
  if (!platform) return [];
  if (!slipOptionsCache.has(platform.label)) slipOptionsCache.set(platform.label, buildSlipTypeOptions(platform));
  return slipOptionsCache.get(platform.label);
}

function buildSlipTypeOptions(platform) {
  if (platform.kind === 'perPick') {
    return [{ key: 'perPick', label: 'Per-pick payouts', be: 1 }];
  }
  const opts = [];
  for (const [n, mult] of Object.entries(platform.power || {})) {
    opts.push({ key: `power-${n}`, label: `${n}-pick Power`, be: slipBreakEven('power', +n, mult) });
  }
  for (const [n, tiers] of Object.entries(platform.flex || {})) {
    const be = slipBreakEven('flex', +n, tiers);
    if (be != null) opts.push({ key: `flex-${n}`, label: `${n}-pick Flex`, be });
  }
  return opts.sort((a, b) => a.be - b.be);
}

// Breakeven win rate for one pick quoted against a slip type
export function legBreakEven(slipOption, multiplier) {
  if (!slipOption || !(multiplier > 0)) return null;
  return slipOption.be / multiplier;
}

// Breakeven at the platform's cheapest slip type — the fixed reference used
// wherever one number per pick is needed (Sharp Money, the 8% EV filter)
export const referenceBreakEven = (platform, multiplier) =>
  legBreakEven(slipTypeOptions(platform)[0], multiplier);

// ── Feed rows ──

const isMultiplierFeed = (bookName) => /Multipliers|Demons/.test(bookName || '');
const lineKey = (row) =>
  `${row.fixture_id}|${row.market_id}|${row.playerId || ''}|${row.points}|${(row.side || '').toLowerCase()}`;

/**
 * One row per pick'em line per platform. OpticOdds sends some lines twice — a
 * platform's standard feed (e.g. PrizePicks Flex, DraftKings Pick 6) and a 1x
 * copy on its multiplier feed — so the multiplier copy is dropped when the
 * standard feed has the same line. Rows are odds-store rows (bookName, price,
 * fixture_id, market_id, playerId, points, side). `multiplierFor` is the
 * pickemMultiplierLookup() result for these rows, so the 1x test is the one
 * the pages use; it is built here when not passed in.
 */
export function dedupePickemRows(rows, multiplierFor = pickemMultiplierLookup(rows)) {
  const standard = new Set();
  for (const row of rows) {
    if (!isMultiplierFeed(row.bookName)) standard.add(`${platformForBook(row.bookName)?.label}|${lineKey(row)}`);
  }
  return rows.filter(row => !(isMultiplierFeed(row.bookName)
    && multiplierFor(row) === 1
    && standard.has(`${platformForBook(row.bookName)?.label}|${lineKey(row)}`)));
}

// ── Whole-slip results ──

/**
 * @param platform  entry from PICKEM_PLATFORMS
 * @param playType  'power' | 'flex'
 * @param legs      [{ p: model win prob, m: pick multiplier }]
 * @returns {
 *   allHitProb, fairOdds (American),
 *   payout:  entry-fee multiple if every pick hits (null if unknown/unavailable),
 *   tiers:   [{ hits, payout, prob }] — Flex partial payouts,
 *   ev:      expected profit per $1 (null if unknown),
 *   evIsFloor: payout can come in higher (DraftKings Extra Winnings),
 *   note:    why payout/EV is missing, if it is
 * }
 * Flex with boosted picks scales every tier by the product of multipliers —
 * an approximation; the apps don't publish how boosts apply to partial hits.
 */
export function slipResult(platform, playType, legs) {
  const n = legs.length;
  const probs = legs.map(l => l.p);
  const allHitProb = probs.reduce((acc, p) => acc * p, 1);
  const base = {
    allHitProb, fairOdds: probToAmerican(allHitProb), payout: null, tiers: [], ev: null,
    evIsFloor: !!platform?.extraWinnings, note: null,
  };
  if (n === 0 || !platform) return base;
  if (legs.some(l => !(l.m > 0))) return { ...base, note: 'Unknown pick multiplier' };
  const multProduct = legs.reduce((acc, l) => acc * l.m, 1);

  if (platform.kind === 'perPick') {
    if (n < 2) return { ...base, note: 'Needs 2+ picks' };
    return { ...base, payout: multProduct, ev: allHitProb * multProduct - 1 };
  }
  const sizes = Object.keys((playType === 'flex' ? platform.flex : platform.power) || {}).map(Number);
  if (sizes.length && n < Math.min(...sizes)) return { ...base, note: `Needs ${Math.min(...sizes)}+ picks` };
  if (playType === 'flex') {
    const table = platform.flex?.[n];
    if (!table) return { ...base, note: `Flex not offered for ${n} picks` };
    const dist = hitDistribution(probs);
    const tiers = Object.keys(table).map(Number).sort((a, b) => b - a)
      .map(hits => ({ hits, payout: table[hits] * multProduct, prob: dist[hits] }));
    const expected = tiers.reduce((sum, t) => sum + t.prob * t.payout, 0);
    return { ...base, payout: table[n] * multProduct, tiers, ev: expected - 1 };
  }
  const mult = platform.power?.[n];
  if (!mult) {
    return { ...base, note: platform.partialTable ? `${n}-pick payout not published` : `Power not offered for ${n} picks` };
  }
  const payout = mult * multProduct;
  return { ...base, payout, ev: allHitProb * payout - 1 };
}
